import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDialogue } from '../contexts/DialogueContext';
import { useRecording } from '../contexts/RecordingContext';
import DialogueItem from './DialogueItem';

export default function DialogueMenu({ onClose }) {
  const {
    currentDialogueId,
    dialogueList,
    loadingDialogues,
    switchDialogue,
    renameDialogue,
    archiveDialogue,
    loadDialogues
  } = useDialogue();

  const {
    setMessages,
    loadDialogueMessages,
    setPendingTransactions,
    setNewIssueIds
  } = useRecording();

  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const listRef = useRef(null);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTouchStart = useCallback((e) => {
    const el = listRef.current;
    if (el && el.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.5, 80));
    }
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance > 50) {
      setRefreshing(true);
      setPullDistance(0);
      await loadDialogues();
      setRefreshing(false);
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, loadDialogues]);

  const handleSwitch = async (dialogueId) => {
    if (dialogueId === currentDialogueId) {
      // Already on this dialogue, just close menu
      onClose();
      return;
    }

    // Switch dialogue and load its messages
    await switchDialogue(dialogueId, (messages, transactions) => {
      // Convert Firestore messages to frontend format
      const formattedMessages = messages.map(msg => ({
        id: msg.id,
        sender: msg.role === 'user' ? 'me' : 'bot',
        text: msg.content,
        ts: msg.created_at,
        // If it was an audio transcript, we could add audioUrl here
        // but we don't store the audio blob in Firestore
      }));

      loadDialogueMessages(formattedMessages, dialogueId);
      setPendingTransactions(transactions);
      setNewIssueIds(new Set());
    });

    // Close menu
    onClose();
  };

  const handleRename = async (dialogueId, newName) => {
    await renameDialogue(dialogueId, newName);
  };

  const handleDelete = async (dialogueId) => {
    const isCurrentDialogue = dialogueId === currentDialogueId;
    const success = await archiveDialogue(dialogueId);

    if (success) {
      // Reload dialogue list
      await loadDialogues();

      // If we deleted the current dialogue, switch to another one
      if (isCurrentDialogue) {
        // Find another dialogue to switch to (first one that's not the deleted one)
        const remainingDialogue = dialogueList.find(d => d.id !== dialogueId);

        if (remainingDialogue) {
          // Switch to the remaining dialogue
          await handleSwitch(remainingDialogue.id);
        } else {
          // No dialogues left, clear messages
          setMessages([]);
          setPendingTransactions([]);
          setNewIssueIds(new Set());
        }
      }
    }
  };

  // Determine how many dialogues to show (5 on mobile, 10 on desktop)
  const maxVisible = isMobile ? 5 : 10;
  const visibleDialogues = expanded ? dialogueList : dialogueList.slice(0, maxVisible);
  const hasMore = dialogueList.length > maxVisible;

  return (
    <div
      className="dialogue-menu"
      ref={listRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {(pullDistance > 0 || refreshing) && (
        <div className="pull-to-refresh" style={{ textAlign: 'center', padding: '8px 0', fontSize: '13px', color: '#888', transition: 'opacity 0.2s', opacity: pullDistance > 30 || refreshing ? 1 : pullDistance / 30 }}>
          {refreshing ? 'Refreshing...' : pullDistance > 50 ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      )}
      {loadingDialogues && !refreshing ? (
        <div className="dialogue-loading">Loading conversations...</div>
      ) : dialogueList.length === 0 ? (
        <div className="dialogue-empty">
          <p>No conversations yet.</p>
          <p className="dialogue-empty-hint">Start by sending a message!</p>
        </div>
      ) : (
        <>
          <div className="dialogue-list">
            {visibleDialogues.map(dialogue => (
              <DialogueItem
                key={dialogue.id}
                dialogue={dialogue}
                isActive={dialogue.id === currentDialogueId}
                onSwitch={() => handleSwitch(dialogue.id)}
                onRename={(newName) => handleRename(dialogue.id, newName)}
                onDelete={() => handleDelete(dialogue.id)}
              />
            ))}
          </div>

          {hasMore && !expanded && (
            <button
              className="dialogue-show-more"
              onClick={() => setExpanded(true)}
            >
              ⋯ Show {dialogueList.length - maxVisible} more
            </button>
          )}
        </>
      )}
    </div>
  );
}
